import { ChooseStoreCard } from '../choose-store-card/choose-store-card'
import {
  Secondary,
  TextField,
  Text,
  SvgIcon,
  Alert,
  FormLabel,
} from '@cinch-labs/design-system'
import { useState, useEffect, useCallback, useRef, ReactNode } from 'react'
import { round } from 'lodash'
import styles from './find-a-store.module.css'
import { useFetchAllStoresAndCalculateDistance } from '../../hooks/fetch-stores-with-distance'
import { LoadingSpinner } from '@cinch-nx/shared-ui'
import {
  EventNames,
  TrackingEventTypes,
  useAnalytics,
} from '@cinch-nx/shared-analytics'
import { StoreMap } from './find-a-store-map'
import { StoreWithDistance } from '../../hooks/types'
import useIsMobile from '../../hooks/use-is-mobile'
import cx from 'classnames'
import { MobileMapOverlay } from '../mobile-map-overlay/mobile-map-overlay'
import { formatAddress } from '../../utils/store-card-details'

const METERS_TO_MILES_CONVERSION = 0.000_621_371
const UK_POSTCODE_REGEX = /^([A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2})$/i

export const toMiles = (meters: number) =>
  round(meters * METERS_TO_MILES_CONVERSION, 1)

export type FindAStoreListBannerContext = {
  nearestMatchingStoreWithin20Miles?: StoreWithDistance
  storesWithDistance: StoreWithDistance[]
  visibleStores: StoreWithDistance[]
}

export type FindAStoreProps = {
  initialPostcode?: string
  mobileInitialVisibleCount?: number
  under60Months?: boolean
  vehicleMake?: string
  analyticsCategory?: string
  searchButtonLabel?: string
  infoAlert?: ReactNode
  renderListBanner?: (context: FindAStoreListBannerContext) => ReactNode
  onSelectStore: (
    storeWithDistance: StoreWithDistance,
    postcode: string,
  ) => void
  onNearestOemStoreChange?: (store: StoreWithDistance | undefined) => void
}

export const FindAStore: React.FC<FindAStoreProps> = ({
  initialPostcode,
  mobileInitialVisibleCount = 2,
  under60Months,
  vehicleMake,
  analyticsCategory = 'cinch_servicing',
  searchButtonLabel = 'Find service centre',
  infoAlert,
  renderListBanner,
  onSelectStore,
  onNearestOemStoreChange,
}) => {
  const [postCode, setPostCode] = useState('')
  const { trackEvent } = useAnalytics()
  const [shouldFetch, setShouldFetch] = useState(false)
  const [visibleCount, setVisibleCount] = useState(0)
  const [inputError, setInputError] = useState('')
  const [selectedStoreOnMap, setSelectedStoreOnMap] =
    useState<StoreWithDistance>()
  const [mapHidden, setMapHidden] = useState(false)
  const [showMobileMapOverlay, setShowMobileMapOverlay] = useState(false)
  const [mobileMapSelectedStore, setMobileMapSelectedStore] =
    useState<StoreWithDistance>()
  const userInteracted = useRef(false)
  const { isMobile } = useIsMobile()
  const initialVisibleCount = isMobile ? mobileInitialVisibleCount : 2

  const {
    storesWithDistance,
    nearestMatchingStoreWithin20Miles,
    customerCoordinates,
    loading,
    error,
  } = useFetchAllStoresAndCalculateDistance(
    shouldFetch ? postCode : undefined,
    under60Months,
    vehicleMake,
  )

  useEffect(() => {
    onNearestOemStoreChange?.(nearestMatchingStoreWithin20Miles)
  }, [nearestMatchingStoreWithin20Miles, onNearestOemStoreChange])

  useEffect(() => {
    if (error === 'Postcode') {
      setInputError('Please enter a valid postcode (e.g. RG27 9XA).')
    } else if (error === 'Stores') {
      setInputError(
        'An error occurred while finding service centres. Please check your postcode and try again.',
      )
    } else {
      setInputError('')
    }
  }, [error])

  useEffect(() => {
    if (initialPostcode && !postCode && !userInteracted.current) {
      const trimmed = initialPostcode.trim()
      if (UK_POSTCODE_REGEX.test(trimmed)) {
        setPostCode(trimmed)
        setShouldFetch(true)
        setVisibleCount(initialVisibleCount)
      }
    }
  }, [initialPostcode, initialVisibleCount, postCode])

  const visibleStores = storesWithDistance.slice(0, visibleCount)
  const remaining = storesWithDistance.length - visibleCount

  const handleChange = useCallback((value: string) => {
    userInteracted.current = true
    setInputError('')
    setShouldFetch(false)
    setPostCode(value)
  }, [])

  const onSubmit = () => {
    trackEvent({
      eventName: `Search Stores`,
      type: TrackingEventTypes.ADOBE,
      data: {
        event: {
          name: EventNames.click,
          category: analyticsCategory,
          action: 'search_store',
          label: 'make_a_booking',
        },
      },
    })
    const trimmed = postCode.trim()
    if (!UK_POSTCODE_REGEX.test(trimmed)) {
      setInputError('Please enter a valid postcode (e.g. RG27 9XA).')
      return
    }
    setInputError('')
    setShouldFetch(true)
    setVisibleCount(initialVisibleCount)
  }

  const onShowStoreOnMapClick = (store: StoreWithDistance) => {
    trackEvent({
      eventName: `Show Store On Map`,
      type: TrackingEventTypes.ADOBE,
      data: {
        event: {
          name: EventNames.click,
          category: analyticsCategory,
          action: isMobile ? 'open_mobile_map_overlay' : 'show_store_on_map',
          label: store.store.name,
        },
      },
    })

    if (isMobile) {
      setMobileMapSelectedStore(store)
      setShowMobileMapOverlay(true)
    } else {
      setSelectedStoreOnMap(store)
      setMapHidden(false)
    }
  }

  const onToggleHideMapClick = () => {
    trackEvent({
      eventName: `Toggle Store Map`,
      type: TrackingEventTypes.ADOBE,
      data: {
        event: {
          name: EventNames.click,
          category: analyticsCategory,
          action: 'toggle_map',
          label: mapHidden ? 'show_map' : 'hide_map',
        },
      },
    })

    setMapHidden(!mapHidden)
  }

  const showMore = () => {
    if (isMobile) {
      trackEvent({
        eventName: 'Show More Service Centres',
        type: TrackingEventTypes.ADOBE,
        data: {
          event: {
            name: EventNames.click,
            category: analyticsCategory,
            action: 'show_more_service_centres',
            label: 'show more service centres',
          },
        },
      })
    }

    setVisibleCount(visibleCount + 5)
    setSelectedStoreOnMap(undefined)
  }

  useEffect(() => {
    if (selectedStoreOnMap) {
      const storeList = window.document.querySelector('[id="stores-list"]')

      storeList?.scrollTo({
        top: 280 * selectedStoreOnMap.index,
        behavior: 'smooth',
      })
    }
  }, [selectedStoreOnMap])

  const handleSelectStore = useCallback(
    (storeWithDistance: StoreWithDistance) => {
      window.scrollTo({ top: 0, behavior: 'auto' })
      onSelectStore(storeWithDistance, postCode.trim().toUpperCase())
    },
    [onSelectStore, postCode],
  )

  const canShowMap = !isMobile && !!customerCoordinates && !!postCode
  const showingMap = !!canShowMap && !mapHidden

  const listBanner = renderListBanner?.({
    nearestMatchingStoreWithin20Miles,
    storesWithDistance,
    visibleStores,
  })

  return (
    <>
      {infoAlert ?? (
        <Alert type="info" displayPanelVariant className={styles.infoAlert}>
          <Text element="p" fontSizeFixed="s" fontWeight="semibold">
            Any make and model
          </Text>
          <Text element="p" fontSizeFixed="s">
            You can take your car to any of our service centres.
          </Text>
        </Alert>
      )}
      <div className={styles.container} data-testid="find-store">
        <div className={styles.formContainer}>
          <FormLabel
            htmlFor="postcode_input"
            classNames={{ label: styles.postcodeLabel }}
          >
            <Text
              fontSize="s"
              fontWeight="semibold"
              element="span"
              className={styles.postcodeLabelText}
            >
              Postcode
            </Text>
          </FormLabel>
          <div className={styles.postcodeRow}>
            <div className={styles.postcodeInputWrap}>
              <span className={styles.postcodeIcon} aria-hidden="true">
                <SvgIcon name="location" size={18} />
              </span>
              <TextField
                id="postcode_input"
                name="Postcode"
                value={postCode}
                placeholder="RG27 9XA"
                type="text"
                label="Postcode"
                labelHidden
                aria-label="Postcode"
                onChange={(e) => handleChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                    onSubmit()
                  }
                }}
                fullWidth
                error={{
                  show: !!inputError,
                  message: inputError,
                  collapse: true,
                }}
                classNames={{
                  container: styles.formTextField,
                  input: styles.formTextFieldInput,
                }}
              />
            </div>
            <Secondary
              onClick={onSubmit}
              aria-label={searchButtonLabel}
              size="large"
              classNames={{ button: styles.formButton }}
              data-testid="find-store-button"
            >
              {searchButtonLabel}
            </Secondary>
          </div>
        </div>
        {loading && <LoadingSpinner size={50} />}
        <div
          className={cx(
            styles.mainContainer,
            showingMap ? styles.mainContainerWide : '',
          )}
        >
          <div className={showingMap ? styles.mainSubContainerWide : ''}>
            {visibleStores.length > 0 && canShowMap && (
              <div
                className={cx(
                  styles.storeOptionHeader,
                  showingMap ? styles.storeOptionHeaderWide : '',
                )}
              >
                <Text
                  element="h2"
                  fontSize="m"
                  fontSizeFixed="m"
                  fontWeight="semibold"
                  id="choose-store-heading"
                >
                  Choose one option
                </Text>
                <button
                  type="button"
                  className={styles.viewMapButton}
                  onClick={onToggleHideMapClick}
                >
                  {mapHidden ? (
                    <>
                      <SvgIcon name="location" size={14} />
                      View map
                    </>
                  ) : (
                    <>
                      <SvgIcon name="cross" size={14} />
                      Close map
                    </>
                  )}
                </button>
              </div>
            )}

            {visibleStores.length > 0 && !canShowMap && (
              <div className={styles.storeOptionHeader}>
                <Text
                  element="h2"
                  fontSize="m"
                  fontSizeFixed="m"
                  fontWeight="semibold"
                  id="choose-store-heading"
                >
                  Choose one option
                </Text>
                {isMobile && (
                  <button
                    type="button"
                    className={styles.viewMapButton}
                    onClick={() => {
                      setMobileMapSelectedStore(storesWithDistance[0])
                      setShowMobileMapOverlay(true)
                      trackEvent({
                        eventName: `View on Map`,
                        type: TrackingEventTypes.ADOBE,
                        data: {
                          event: {
                            name: EventNames.click,
                            category: analyticsCategory,
                            action: 'view_on_map_link',
                          },
                        },
                      })
                    }}
                  >
                    <SvgIcon name="location" size={14} />
                    View map
                  </button>
                )}
              </div>
            )}

            <div className={styles.storesContainer}>
              <div
                data-testid={'stores-list'}
                id={'stores-list'}
                className={showingMap ? styles.storesListContainer : ''}
              >
                {listBanner}
                {visibleStores.map(({ store, distance, index }, i) => (
                  <ChooseStoreCard
                    index={i}
                    key={`${store.id}-${i}`}
                    distance={toMiles(distance)}
                    name={store.name}
                    displayName={store.displayName}
                    address={formatAddress(store)}
                    coordinates={store.coordinates}
                    onSelect={() =>
                      handleSelectStore({ store, distance, index })
                    }
                    isSelected={store.id === selectedStoreOnMap?.store.id}
                    onMapClick={() =>
                      onShowStoreOnMapClick({ store, distance, index })
                    }
                    showMapLink={canShowMap}
                    customerPostcode={postCode}
                    ariaLabel={`Choose cinch Store ${store.name}`}
                    openingHours={store.openingHours}
                    analyticsCategory={analyticsCategory}
                    under60Months={under60Months || false}
                    distanceFromOEM={
                      nearestMatchingStoreWithin20Miles
                        ? toMiles(nearestMatchingStoreWithin20Miles.distance)
                        : undefined
                    }
                  />
                ))}
              </div>
              {canShowMap && !mapHidden && (
                <div className={styles.mapContainer}>
                  <StoreMap
                    stores={visibleStores}
                    customerLocation={{
                      latitude: customerCoordinates?.latitude,
                      longitude: customerCoordinates?.longitude,
                      postcode: postCode,
                    }}
                    selectedStore={selectedStoreOnMap?.store}
                    setSelectedStore={setSelectedStoreOnMap}
                    analyticsCategory={analyticsCategory}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        {visibleStores.length > 0 && remaining > 0 && (
          <Secondary
            onClick={showMore}
            aria-label={`Show more service centres`}
            classNames={{ button: styles.showMoreButton }}
            size="large"
          >
            {`Show more service centres`}
          </Secondary>
        )}
      </div>

      {isMobile && showMobileMapOverlay && (
        <MobileMapOverlay
          stores={storesWithDistance}
          selectedStore={mobileMapSelectedStore}
          onClose={() => setShowMobileMapOverlay(false)}
          onSelectStore={(store) => {
            setShowMobileMapOverlay(false)
            handleSelectStore(store)
          }}
        />
      )}
    </>
  )
}
